import { Button, Heading, Page } from '@ui/themed';
import { t } from 'i18next';
import type { ReactNode } from 'react';
import { getActiveBillingPlanKey } from '../../../constants/features';
import { isAfterBFSReviewSubmission } from '../../../constants/shopify';
import { isShopify, useEnv } from '../../../context/env-context';
import { useHandlers, useSite, withPageProviders } from '../../../context/page-context';
import { useSettings } from '../../../context/settings-context';
import { BillingTrialBadge } from '../components/billing-trial-badge';
import { PostLaunchPage } from './post-launch-page';
import { PreLaunchPage } from './pre-launch-page';

export const HomePage = withPageProviders(HomePageContent);

type HomePageProps = {
  preLaunchHeaderSlot?: ReactNode;
  postLaunchHeaderSlot?: ReactNode;
};

function HomePageContent({ preLaunchHeaderSlot, postLaunchHeaderSlot }: HomePageProps) {
  const env = useEnv();
  const { platform } = useSite();
  const { hasLaunched = true } = useSettings();
  const { navigationHandler } = useHandlers();

  const activeBillingPlanKey = getActiveBillingPlanKey(env.billing.plan_key);

  return (
    <Page>
      {/* Custom header as its doing more than our Page.Header allows */}
      <header className="flex flex-col flex-nowrap gap-4 sm:flex-row sm:flex-nowrap sm:items-center sm:justify-between">
        <Heading level="h1" style="xl">
          {t('features:home.pages.home_page.header.title')}
        </Heading>
        <div className="hidden flex-nowrap items-center justify-end gap-2 sm:flex">
          <div className="hidden md:block">
            <BillingTrialBadge
              activeBillingPlanKey={activeBillingPlanKey}
              trialEndsInDays={env.billing.trial_ends_in_days}
            />
          </div>
          {isShopify(platform) && !isAfterBFSReviewSubmission(platform, env.created_at) && (
            <Button
              onClick={() =>
                navigationHandler.navigate('', {
                  openInDashboard: true,
                })
              }
              variant="secondary"
            >
              {t('buttons:go_to_dashboard')}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() =>
              navigationHandler.navigate('https://docs.sparklayer.io/whats-new', {
                newTab: true,
                absolute: true,
              })
            }
          >
            {t('buttons:whats_new')}
          </Button>
        </div>
      </header>
      {!hasLaunched ? (
        <PreLaunchPage>{preLaunchHeaderSlot}</PreLaunchPage>
      ) : (
        <PostLaunchPage>{postLaunchHeaderSlot}</PostLaunchPage>
      )}
    </Page>
  );
}
